import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Link } from "react-router-dom";
import { format, subDays } from "date-fns";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  Phone,
  PhoneCall,
  CalendarDays,
  Target,
  Handshake,
  Wallet,
  Trophy,
  FileText,
  Flame,
} from "lucide-react";

import OnboardingBanner from "@/components/OnboardingBanner";
import OnboardingResumeBanner from "@/components/OnboardingResumeBanner";
import { MyRankTrendBanner } from "./MyRankTrendBanner";
import { KpiCard } from "./KpiCard";
import { SyncPill } from "@/components/SyncPill";
import {
  CashCell, ClosePercentCell, SetsCell, ShowPercentCell,
} from "@/pages/leaderboard/MetricBreakdownTooltips";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated subset of KPIs — the ones a rep actually cares about daily.
const REP_KPIS = [
  { key: "dials", label: "DIALS", icon: Phone, format: "number" },
  { key: "connects", label: "CONNECTS", icon: PhoneCall, format: "number" },
  { key: "sets_booked", label: "SETS", icon: CalendarDays, format: "number" },
  { key: "shows", label: "SHOWS", icon: Target, format: "number", color: "text-green-400" },
  { key: "deals_closed", label: "DEALS", icon: Handshake, format: "number" },
  { key: "setter_cash", label: "MY CASH", icon: Wallet, format: "currency" },
];

export default function RepView() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const start_date = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const end_date = format(new Date(), "yyyy-MM-dd");

  const fetchData = useCallback(async () => {
    if (!user?.user_id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: user.user_id,
        start_date,
        end_date,
      });
      const [statsRes, lbRes] = await Promise.all([
        fetch(`${API}/dashboard/stats?${params}`, { credentials: "include" }),
        fetch(`${API}/leaderboard`, { credentials: "include" }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (lbRes.ok) setLeaderboard(await lbRes.json());
      setLastSyncedAt(Date.now());
    } catch (e) {
      console.error("rep dashboard fetch failed:", e);
    } finally {
      setLoading(false);
    }
  }, [user?.user_id, start_date, end_date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const top5 = leaderboard.slice(0, 5);
  const myRow = leaderboard.find(r => r.user_id === user?.user_id);
  const myIndex = leaderboard.findIndex(r => r.user_id === user?.user_id);
  const myRank = myIndex >= 0 ? myIndex + 1 : null;

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-4 sm:space-y-6" data-testid="rep-dashboard-view">
      <OnboardingResumeBanner />
      <OnboardingBanner />
      <MyRankTrendBanner />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-white font-heading">
            {user?.name ? `Hey, ${user.name.split(" ")[0]}` : "Welcome back"}
          </h1>
          <p className="text-gray-400 mt-1 text-xs sm:text-sm">Your last 30 days at a glance</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button asChild variant="outline" size="sm" data-testid="rep-submit-eod-btn" className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            <Link to="/eod"><FileText className="w-4 h-4 mr-2" />Submit EOD</Link>
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="rep-view-leaderboard-btn" className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            <Link to="/leaderboard"><Trophy className="w-4 h-4 mr-2" />Leaderboard</Link>
          </Button>
          <SyncPill
            lastSyncedAt={lastSyncedAt}
            loading={loading}
            onRefresh={fetchData}
            testId="rep-dashboard-sync-pill"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={`rep-kpi-skel-${i}`} className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5 animate-pulse">
              <div className="h-4 w-20 bg-white/10 rounded mb-4" />
              <div className="h-10 w-24 bg-white/10 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {REP_KPIS.map(kpi => (
            <KpiCard
              key={kpi.key}
              label={kpi.label}
              value={stats?.[kpi.key]}
              icon={kpi.icon}
              format={kpi.format}
              color={kpi.color}
            />
          ))}
        </div>
      )}

      {/* My Standing + Top 5 side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-testid="rep-my-standing">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="w-4 h-4 text-amber-400" />
            <h3 className="text-white font-semibold">My Standing</h3>
          </div>
          {myRank ? (
            <>
              <div className="text-4xl font-bold text-white leading-none mb-1">#{myRank}</div>
              <p className="text-xs text-gray-500 mb-4">of {leaderboard.length} reps</p>
              <div className="space-y-1 text-sm" data-testid="rep-my-standing-breakdown">
                <div className="flex justify-between text-gray-300 items-center">
                  <span>Cash</span>
                  <CashCell rep={myRow} />
                </div>
                <div className="flex justify-between text-gray-300 items-center">
                  <span>Sets</span>
                  <SetsCell rep={myRow} />
                </div>
                <div className="flex justify-between text-gray-300 items-center">
                  <span>Deals</span>
                  <span className="text-white font-medium">{myRow?.deals || 0}</span>
                </div>
                <div className="flex justify-between text-gray-300 items-center">
                  <span>Show %</span>
                  <ShowPercentCell rep={myRow} />
                </div>
                <div className="flex justify-between text-gray-300 items-center">
                  <span>Close %</span>
                  <ClosePercentCell rep={myRow} />
                </div>
              </div>
              <p className="mt-3 text-[10px] text-gray-500 leading-tight">
                Hover any number to see the math behind it.
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500">Not yet ranked — submit your first activities to appear on the leaderboard.</p>
          )}
        </div>

        <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-testid="rep-top5-leaderboard">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-emerald-400" />
            <h3 className="text-white font-semibold">Top 5 This Period</h3>
          </div>
          {top5.length === 0 ? (
            <p className="text-sm text-gray-500">Leaderboard empty for the current period.</p>
          ) : (
            <div className="space-y-2">
              {top5.map((row, idx) => {
                const isMe = row.user_id === user?.user_id;
                return (
                  <div key={row.user_id} className={`flex items-center gap-3 py-2 px-3 rounded-md ${isMe ? "bg-emerald-500/10 border border-emerald-500/20" : "hover:bg-white/5"}`}>
                    <span className={`w-6 text-center text-sm font-semibold ${idx === 0 ? "text-amber-400" : "text-gray-500"}`}>#{idx + 1}</span>
                    <span className={`flex-1 text-sm ${isMe ? "text-emerald-300 font-medium" : "text-white"}`}>
                      {row.name}{isMe && " (you)"}
                    </span>
                    <span className="text-sm text-gray-300">${(row.cash || 0).toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
}
